import React, { useState } from "react";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import AInput from "views/pages/manage/common/formFeilds/customInput/AInput";
import CustomDatePickerV2 from "views/pages/manage/common/commonComponents/CustomDatePicker-V2";
import DocUploadModal from "views/pages/manage/unAssigned/studentCharges/studentChargesDetails/paymentMode/DocUploadModal";
import UploadDocument from "views/pages/manage/edpOps/studentDetails/paymentSummary/studentBankDetails/UploadDocument";
import SectionCard from "./SectionCard";
import { agreementTypeDropdown, inputHeight } from "./utils";
import moment from "moment";
import { MdOutlineHandshake } from "react-icons/md";
import { AiOutlineEdit, AiOutlineDelete } from "react-icons/ai";
import { getFormattedDate } from "views/pages/manage/common/utils/methods/commonMethods/utilityMethod";

const EMPTY_AGREEMENT = {
  type: null,
  effectiveDate: moment(),
  expiryDate: "", // auto-populated (effectiveDate + 1 year), not editable
  attachmentKey: "", // s3 key — matches DocUploadModal/UploadDocument pattern
  attachmentName: "", // file name for display
};

const AgreementsSection = ({ agreements = [], onChange }) => {
  const [newRow, setNewRow] = useState({ ...EMPTY_AGREEMENT });
  const [newRowError, setNewRowError] = useState({});
  const [editIndex, setEditIndex] = useState(null);

  // Controls DocUploadModal open/close per row
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const isAgreementType = newRow?.type?.value === "AGREEMENT";

  // ── Generic field updater ─────────────────────────────────────────
  const handleRowChange = (key, value) => {
    setNewRow((prev) => {
      const updated = { ...prev, [key]: value };

      // Auto-set expiry = effectiveDate + 1 year
      if (key === "effectiveDate" && value && isAgreementType) {
        updated.expiryDate = moment(value, "DD-MMM-YYYY")
          .add(1, "year")
          .format("DD-MMM-YYYY");
      }

      // Reset date fields when type changes
      if (key === "type") {
        updated.effectiveDate = moment();
        updated.expiryDate = "";
      }

      return updated;
    });
    setNewRowError((prev) => ({ ...prev, [key]: "" }));
  };

  // ── DocUploadModal saveHandler ────────────────────────────────────
  // Called with s3Key after successful upload
  const handleAttachmentSave = (s3Key, fileName) => {
    setUploadDialogOpen(false);
    handleRowChange("attachmentKey", s3Key);
    handleRowChange("attachmentName", fileName || "Attachment");
    setNewRowError((prev) => ({ ...prev, attachmentKey: "" }));
  };

  // ── Validate row before adding ────────────────────────────────────
  const validateRow = () => {
    const errors = {};
    if (!newRow.type?.value) errors.type = "Type is required";
    if (newRow.type?.value === "AGREEMENT" && !newRow.effectiveDate)
      errors.effectiveDate = "Effective date is required for Agreement type";
    if (!newRow.attachmentKey) errors.attachmentKey = "Attachment is required";
    setNewRowError(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Add or update row ─────────────────────────────────────────────
  const handleAdd = () => {
    if (!validateRow()) return;

    if (editIndex !== null) {
      const updated = [...agreements];
      updated[editIndex] = { ...newRow };
      onChange(updated);
      setEditIndex(null);
    } else {
      onChange([...agreements, { ...newRow }]);
    }
    setNewRow({ ...EMPTY_AGREEMENT });
  };

  const handleEdit = (index) => {
    setNewRow({ ...agreements[index] });
    setEditIndex(index);
  };

  const handleDelete = (index) => {
    const updated = agreements.filter((_, i) => i !== index);
    onChange(updated);
    if (editIndex === index) {
      setNewRow({ ...EMPTY_AGREEMENT });
      setEditIndex(null);
    }
  };

  return (
    <SectionCard icon={<MdOutlineHandshake />} title="Agreements">
      {/* ── Input row ── */}
      <div className="row align-items-end mb-3">
        {/* Type */}
        <div className="form-group col-md-2 mb-2">
          <AAutoComplete
            label="Type *"
            currentValue={newRow.type}
            items={agreementTypeDropdown}
            isMulti={false}
            handleChange={(v) => handleRowChange("type", v)}
            height="44px"
            labelStyle={{ color: "#262626" }}
          />
          {newRowError.type && (
            <span style={{ color: "#d32f2f", fontSize: 11 }}>
              {newRowError.type}
            </span>
          )}
        </div>

        {/* Effective Date — enabled only for Agreement type */}
        <div className="form-group col-md-2 mb-2">
          <CustomDatePickerV2
            label="Effective Date"
            value={newRow.effectiveDate}
            handler={(index, date) => handleRowChange("effectiveDate", date)}
            maxDate={new moment(new Date())}
            disabled={!isAgreementType}
          />
          {newRowError.effectiveDate && (
            <span style={{ color: "#d32f2f", fontSize: 11 }}>
              {newRowError.effectiveDate}
            </span>
          )}
        </div>

        {/* Expiry Date — auto-populated, always disabled */}
        <div className="form-group col-md-2 mb-2">
          <AInput
            label="Expiry Date"
            value={newRow.expiryDate}
            disabled={true}
            style={{ ...inputHeight, background: "#F5F5F5" }}
          />
        </div>

        {/* Attachment — UploadDocument trigger + DocUploadModal */}
        <div className="form-group col-md-3 mb-2">
          <UploadDocument
            attachmentKey={newRow.attachmentKey}
            setUploadDialogOpen={setUploadDialogOpen}
            formHandler={handleRowChange}
            label="Add Attachment*"
            inputStyles={inputHeight}
          />
          {newRowError.attachmentKey && (
            <span style={{ color: "#d32f2f", fontSize: 11 }}>
              {newRowError.attachmentKey}
            </span>
          )}
        </div>

        {/* Add / Update button */}
        <div className="form-group col-md-2 mb-2 d-flex align-items-end">
          <AButton
            onClick={handleAdd}
            variant="primary_filled"
            className="button_remove_left_margin"
          >
            {editIndex !== null ? "Update" : "Add"}
          </AButton>
          {editIndex !== null && (
            <AButton
              onClick={() => {
                setNewRow({ ...EMPTY_AGREEMENT });
                setEditIndex(null);
              }}
              variant="outline"
              className="ml-2"
            >
              Cancel
            </AButton>
          )}
        </div>
      </div>

      {/* ── Agreements table ── */}
      {agreements.length > 0 && (
        <div className="table-responsive">
          <table className="table table-bordered" style={{ fontSize: 13 }}>
            <thead style={{ background: "#F5F5F5" }}>
              <tr>
                <th>S.No.</th>
                <th>Type</th>
                <th>Effective Date</th>
                <th>Expiry Date</th>
                <th>Attachment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agreements.map((row, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{row.type?.label || "-"}</td>
                  <td>{getFormattedDate(row.effectiveDate) || "-"}</td>
                  <td>{getFormattedDate(row.expiryDate) || "-"}</td>

                  <td>
                    {row.attachmentKey ? (
                      <span style={{ color: "#00B0F0" }}>
                        📎 {row.attachmentName || "Attachment"}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    <span
                      style={{
                        cursor: "pointer",
                        color: "#555",
                        marginRight: 12,
                      }}
                      onClick={() => handleEdit(index)}
                      title="Edit"
                    >
                      <AiOutlineEdit size={16} />
                    </span>
                    <span
                      style={{ cursor: "pointer", color: "#d32f2f" }}
                      onClick={() => handleDelete(index)}
                      title="Delete"
                    >
                      <AiOutlineDelete size={16} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {agreements.length === 0 && (
        <div
          className="text-center py-3"
          style={{
            color: "#999",
            fontSize: 13,
            border: "1px dashed #DDD",
            borderRadius: 8,
          }}
        >
          No agreements added yet. At least one agreement is required.
        </div>
      )}

      {/* ── DocUploadModal — same as ChequeDetailsFormDialog ── */}
      <DocUploadModal
        open={uploadDialogOpen}
        setOpen={setUploadDialogOpen}
        acceptedFileTypes={[".pdf"]}
        maxUploadFileSize={5}
        serviceName="STOCK_BRANCH"
        uniqueKeyParameter="vendorAgreement"
        uploadedFileS3Key={newRow.attachmentKey}
        uploadedFileName={newRow.attachmentName || "Agreement"}
        modalTitle="Upload Agreement Document"
        saveHandler={(s3Key) =>
          handleAttachmentSave(s3Key, newRow.attachmentName || "Agreement")
        }
        modalProps={{ zIndex: 1500 }}
        key={newRow.attachmentKey}
      />
    </SectionCard>
  );
};

export default AgreementsSection;
